import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MemberAvatar } from '@/components/clubs'
import { Button, ConfirmDialog, Input, Modal } from '@/components/ui'
import { useAuth } from '@/contexts/auth'
import { type ChallengeBoard, useDeleteCategory, useUpdateCategory } from '@/hooks/challenges'
import type { LibraryBook } from '@/lib/insights'
import type { ChallengeCategory, ClubChallenge, ClubMember } from '@/types'
import { EntryRow } from './EntryRow'
import { MyEntryPanel } from './MyEntryPanel'

interface CategoryModalProps {
  challenge: ClubChallenge
  category: ChallengeCategory
  index: number
  board: ChallengeBoard
  members: Map<string, ClubMember>
  canManage: boolean
  myBooks: LibraryBook[]
  onClose: () => void
}

export function CategoryModal({
  challenge,
  category,
  index,
  board,
  members,
  canManage,
  myBooks,
  onClose,
}: CategoryModalProps) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const updateCategory = useUpdateCategory()
  const deleteCategory = useDeleteCategory()
  const [editing, setEditing] = useState(false)
  const [title, setTitle] = useState(category.title)
  const [description, setDescription] = useState(category.description ?? '')
  const [confirmDelete, setConfirmDelete] = useState(false)

  const entries = board.entries.filter((e) => e.category_id === category.id)
  const myEntry = entries.find((e) => e.user_id === user?.id)
  const myEntries = board.entries.filter((e) => e.user_id === user?.id)
  // Mine first, then completed, then the rest
  const sorted = [...entries].sort(
    (a, b) =>
      Number(b.user_id === user?.id) - Number(a.user_id === user?.id) ||
      Number(!!b.completed_on) - Number(!!a.completed_on)
  )
  const suggester = category.suggested_by ? members.get(category.suggested_by) : undefined
  const canEdit = canManage || category.suggested_by === user?.id

  return (
    <Modal isOpen onClose={onClose} title={`#${index + 1} · ${category.title}`} size="lg">
      <div className="space-y-5 overflow-y-auto p-6">
        {editing ? (
          <form
            className="space-y-3"
            onSubmit={async (e) => {
              e.preventDefault()
              if (!title.trim()) return
              await updateCategory.mutateAsync({ category, title, description })
              setEditing(false)
            }}
          >
            <Input value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)} />
            <Input
              value={description}
              maxLength={500}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('challenges.categories.descriptionPlaceholder')}
            />
            <div className="flex gap-2">
              <Button type="submit" size="sm" disabled={updateCategory.isPending}>
                {t('common.save')}
              </Button>
              <Button type="button" size="sm" variant="ghost" onClick={() => setEditing(false)}>
                {t('common.cancel')}
              </Button>
            </div>
          </form>
        ) : (
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 space-y-1">
              {category.description && (
                <p className="whitespace-pre-wrap break-words text-sm text-foreground">
                  {category.description}
                </p>
              )}
              <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                {suggester && (
                  <MemberAvatar
                    userId={suggester.user_id}
                    name={suggester.display_name}
                    avatarUrl={suggester.avatar_url}
                    size="xs"
                  />
                )}
                {t('challenges.categories.suggestedBy', {
                  name: suggester?.display_name ?? t('challenges.formerMember'),
                })}
              </p>
            </div>
            {canEdit && (
              <div className="flex shrink-0 gap-1">
                <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
                  {t('common.edit')}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-error"
                  onClick={() => setConfirmDelete(true)}
                >
                  {t('common.delete')}
                </Button>
              </div>
            )}
          </div>
        )}

        <MyEntryPanel
          challenge={challenge}
          category={category}
          myEntry={myEntry}
          myEntries={myEntries}
          myBooks={myBooks}
        />

        <div className="space-y-3">
          <p className="text-sm font-semibold text-foreground">
            {t('challenges.categories.everyonesPicks', { count: entries.length })}
          </p>
          {sorted.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('challenges.categories.noPicks')}</p>
          ) : (
            <ul className="space-y-3">
              {sorted.map((entry) => (
                <EntryRow
                  key={entry.id}
                  entry={entry}
                  members={members}
                  commentCount={board.commentCounts[entry.id] ?? 0}
                  canModerate={canManage}
                  isMine={entry.user_id === user?.id}
                />
              ))}
            </ul>
          )}
        </div>
      </div>

      <ConfirmDialog
        isOpen={confirmDelete}
        title={t('challenges.categories.deleteTitle')}
        message={t('challenges.categories.deleteMessage', { count: entries.length })}
        confirmText={t('common.delete')}
        variant="danger"
        isLoading={deleteCategory.isPending}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={async () => {
          await deleteCategory.mutateAsync(category)
          setConfirmDelete(false)
          onClose()
        }}
      />
    </Modal>
  )
}
